import { cn } from "@/lib/cn";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { TEXT_SIZES, useTextToneClass, type TextTone } from "./Typography";

/** Classes of `DescriptionList`; `divided` stacks rows edge to edge with a hairline between them. */
export const descriptionListVariants = cva("flex flex-col", {
  variants: {
    gap: { sm: "gap-1.5", md: "gap-2.5" },
    divided: { true: "gap-0 divide-y [&>*]:py-2", false: "" },
  },
  defaultVariants: { gap: "md", divided: false },
});

/** Props of `DescriptionList`. */
export type DescriptionListProps = VariantProps<typeof descriptionListVariants> & HTMLAttributes<HTMLDListElement>;

/** Semantic `dl` holding `KeyValueRow`s. */
export const DescriptionList = forwardRef<HTMLDListElement, DescriptionListProps>(function DescriptionList(
  { gap, divided, className, ...rest },
  ref,
) {
  return <dl {...rest} ref={ref} className={cn(descriptionListVariants({ gap, divided }), className)} />;
});

/** Props of `KeyValueRow`; `emphasis="total"` adds the divider and the bold total, `size` the row's type step. */
export type KeyValueRowProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  label: ReactNode;
  value: ReactNode;
  tone?: TextTone;
  emphasis?: "none" | "total";
  strong?: boolean;
  size?: "sm" | "base";
};

/** Label/value pair (`dt` + `dd`) laid out on one line. */
export const KeyValueRow = forwardRef<HTMLDivElement, KeyValueRowProps>(function KeyValueRow(
  { label, value, tone, emphasis = "none", strong = false, size, className, ...rest },
  ref,
) {
  const isTotal = emphasis === "total";
  const toneClass = useTextToneClass(tone ?? (isTotal || strong ? "default" : "muted"));
  return (
    <div
      {...rest}
      ref={ref}
      className={cn(
        "flex items-baseline justify-between gap-3",
        toneClass,
        size && TEXT_SIZES[size],
        isTotal && "mt-1 border-t border-border pt-3 text-lg font-bold",
        className,
      )}
    >
      <dt className="min-w-0">{label}</dt>
      <dd className={cn("shrink-0 text-right", strong && "font-semibold")}>{value}</dd>
    </div>
  );
});
